import { Box, Card, Collapse, Dialog, DialogContent } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

interface LineData {
  score: number | string;
  peak_position: number | string;
  tc_ratio: number | string;
  t_color?: string; // Color for each T-peak line
}

interface GraphData {
  test_lines: LineData[];        // Data for test lines with T-peaks
  cscore: number | string;       // Score for the C-line
  cpeak: number | string;        // Peak position for the C-line
  c_color?: string;               // Color for the C-peak annotation
  profile: any[];                // Mean profile data
  baseline: any[];               // Mean baseline data
  baseline_color: string;        // Color for the mean baseline series
  profile_color: string;         // Color for the mean profile series
  meanProfileVisible: boolean;   // Whether the mean profile/baseline is visible
  baselines: any[];           // Array of frame data (each frame's baseline)
  profiles: any[];         // Array of frame data (each frame's profile)
}


const ResultDetailGraphBulkReview: React.FC<{ graphdata: GraphData, showGraph: any }> = ({ graphdata, showGraph }) => {
  const [apexChartData, setApexChartData] = useState<any[]>([]);
  const [annotations, setAnnotations] = useState<ApexOptions['annotations']>({
    xaxis: []
  });
  const [annotationsExpanded, setAnnotationsExpanded] = useState<ApexOptions['annotations']>({
    xaxis: []
  });
  const [graphText, setGraphText] = useState<string>("Choose Data to Display");
  const [openGraphDialog, setOpenGraphDialog] = useState<boolean>(false);

  useEffect(() => {
    // Aggregate all series data (mean profile, mean baseline, and dynamic profiles/baselines)
    const allSeries: any[] = [
      ...graphdata.baselines.map((baseline, index) => ({
        ...baseline,
        tooltip: { enabled: false }, // Enable tooltip only for the first 2 series
      })),
      ...graphdata.profiles.map((profile, index) => ({
        ...profile,
        tooltip: { enabled: false }, // Enable tooltip only for the first 2 series
      }))
    ];

    setApexChartData(allSeries);

    // If No data then still display the graph
    // if (allSeries.length === 0) {
    //   setApexChartData([{ name: "No Data", data: [] }]);
    // }

    // Define annotations for C-Peak and T-Peaks
    const dynamicAnnotations: ApexOptions['annotations'] = { xaxis: [] };

    // Add C-Peak if mean profile is active
    if (graphdata.meanProfileVisible && graphdata.cpeak) {
      const intensity = typeof graphdata.cscore === "number"
        ? graphdata.cscore.toFixed(2)
        : parseFloat(graphdata.cscore).toFixed(2);
      dynamicAnnotations.xaxis!.push({
        x: graphdata.cpeak,
        borderColor: graphdata.c_color ? graphdata.c_color : "#000",
        label: {
          borderColor: graphdata.c_color ? graphdata.c_color : "#000",
          orientation: "horizontal",
          style: {
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: openGraphDialog ? "14px" : "10px",
            background: graphdata.c_color ? graphdata.c_color : "#000"
          },
          text: `C\u00A0\u00A0${intensity}`
        }
      });
    }

    // Add T-Peaks for each test line if mean profile is active
    if (graphdata.meanProfileVisible) {
      const total = graphdata.test_lines.length; // total test lines
      graphdata.test_lines.forEach((line, index) => {
        const tc = typeof line.tc_ratio === "number"
          ? line.tc_ratio.toFixed(2)
          : parseFloat(line.tc_ratio).toFixed(2);

        // reverse numbering: highest first down to 1
        const reversedNumber = total - index;

        dynamicAnnotations.xaxis!.push({
          x: line.peak_position,
          borderColor: line.t_color ? line.t_color : "#000",
          label: {
            borderColor: line.t_color ? line.t_color : "#000",
            orientation: "horizontal",
            style: {
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: openGraphDialog ? "14px" : "10px",
              background: line.t_color ? line.t_color : "#000"
            },
            text: `T${reversedNumber}/C\u00A0\u00A0${tc}`
          }
        });
      });
    }

    const dynamicAnnotationsExpanded: ApexOptions['annotations'] = { xaxis: [] };

    // Add C-Peak if mean profile is active
    if (graphdata.meanProfileVisible && graphdata.cpeak) {
      const intensity = typeof graphdata.cscore === "number"
        ? graphdata.cscore.toFixed(2)
        : parseFloat(graphdata.cscore).toFixed(2);
      dynamicAnnotationsExpanded.xaxis!.push({
        x: graphdata.cpeak,
        borderColor: graphdata.c_color ? graphdata.c_color : "#000",
        label: {
          borderColor: graphdata.c_color ? graphdata.c_color : "#000",
          orientation: "horizontal",
          style: {
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: openGraphDialog ? "14px" : "10px",
            background: graphdata.c_color ? graphdata.c_color : "#000"
          },
          text: `C\u00A0\u00A0${intensity}`
        }
      });
    }

    // Add T-Peaks for each test line if mean profile is active
    if (graphdata.meanProfileVisible) {
      const total = graphdata.test_lines.length; // total test lines
      graphdata.test_lines.forEach((line, index) => {
        const intensity = typeof line.score === "number"
          ? line.score.toFixed(2)
          : parseFloat(line.score).toFixed(2);

        // reverse numbering: highest first down to 1
        const reversedNumber = total - index;

        dynamicAnnotationsExpanded.xaxis!.push({
          x: line.peak_position,
          borderColor: line.t_color ? line.t_color : "#000",
          label: {
            borderColor: line.t_color ? line.t_color : "#000",
            orientation: "horizontal",
            style: {
              color: "#ffffff",
              fontWeight: "bold",
              fontSize: openGraphDialog ? "14px" : "10px",
              background: line.t_color ? line.t_color : "#000"
            },
            text: `TL${reversedNumber}\u00A0\u00A0${intensity}`
          }
        });
      });
    }

    setAnnotationsExpanded(dynamicAnnotationsExpanded);
    setAnnotations(dynamicAnnotations);
  }, [graphdata, openGraphDialog]);


  // NEW — tiny, static preview chart options (no interactivity)
  const miniOptions = useMemo<ApexOptions>(() => ({
    noData: {
      text: graphText,
      align: "center",
      verticalAlign: "middle",
      offsetX: 0,
      offsetY: 0,
      style: { fontSize: "16px" }
    },
    chart: {
      id: "preview-area",
      type: "line",
      toolbar: { show: false, tools: { download: false } },
      animations: { enabled: false },
    },
    annotations,                                  // keep your C/T peak markers
    xaxis: { type: "numeric", title: { text: "Profile" } },
    yaxis: {
      min: 0,
      max: 255,
      title: { text: "Intensity" },
      labels: { formatter: (v) => Math.round(v).toString() }
    },
    tooltip: { enabled: false },               // turn off hover work entirely
    // dataLabels: { enabled: false },
    stroke: { width: 2, curve: "smooth" },
    markers: { size: 0 },
    // grid: { show: false },
    legend: { show: false },
    // keep your series colors if you pass them in each series object
    colors: Array.isArray(apexChartData) ? apexChartData.map(s => (s as any).color) : undefined,
  } as ApexOptions), [apexChartData]);

  // NEW — full, interactive chart options for the modal
  const fullOptions = useMemo<ApexOptions>(() => ({
    noData: {
      text: graphText,
      align: "center",
      verticalAlign: "middle",
      offsetX: 0,
      offsetY: 0,
      style: { fontSize: "16px" }
    },
    tooltip: {
      shared: true,
      // keep your custom tooltip for the full chart
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const meanBaseline = series[0][dataPointIndex];
        const meanProfile = series[1]?.[dataPointIndex];
        const intensity = (meanBaseline !== undefined && meanProfile !== undefined)
          ? (meanBaseline - meanProfile).toFixed(2)
          : "N/A";
        const value = series[seriesIndex][dataPointIndex];
        return `<div style="padding: 5px;">
                <strong>${w.globals.seriesNames[seriesIndex]}:</strong> ${Number(value).toFixed(2)}
                <br><strong>Height:</strong> ${intensity}
              </div>`;
      },
    },
    annotations: annotationsExpanded,                                  // keep your C/T peak markers
    colors: Array.isArray(apexChartData) ? apexChartData.map(s => (s as any).color) : undefined,
    stroke: { width: 2, curve: "smooth" },
    chart: {
      id: "dynamic-area",
      type: "line",
      toolbar: { show: true, tools: { download: true } },
      animations: { enabled: false },
      zoom: {
        enabled: true,
        type: 'xy',
        zoomedArea: {
          fill: { color: '#90CAF9', opacity: 0.4 },
          stroke: { color: '#0D47A1', opacity: 0.4, width: 1 }
        }
      },
    },
    xaxis: { type: "numeric", title: { text: "Profile" } },
    yaxis: {
      min: 0,
      max: 255,
      title: { text: "Intensity" },
      labels: { formatter: (v) => Math.round(v).toString() }
    },
    legend: { show: false },
  } as ApexOptions), [apexChartData, annotations, graphText]);

  const handleDialogOpen = () => setOpenGraphDialog(true);
  const handleDialogClose = () => setOpenGraphDialog(false);

  return apexChartData.length > 0 ? (
    <>
      <Box
        onClick={handleDialogOpen}
        sx={{
          width: "100%",
          // p: 1,
          height: "auto",
          position: "relative"
        }}
      >
        <Collapse in={showGraph && apexChartData.length > 0}>

          <Card
            sx={{
              border: "1px #ddd solid",
              borderRadius: 1,
              width: "100%",
              maxWidth: 400,
              p: 2,
              pb: 0,
              position: "relative"
            }}
          >
            <ReactApexChart
              type="line"
              options={miniOptions}
              series={apexChartData}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
              // Stop all internal mouse handlers from firing. Parent Box still catches the click.
              style={{ pointerEvents: 'none', cursor: 'zoom-in' }}
            />

          </Card>
        </Collapse>
      </Box>
      <Dialog
        open={openGraphDialog}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        slotProps={{ backdrop: { onClick: handleDialogClose } }}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "transparent", // Semi-transparent background
            boxShadow: "none",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "transparent",
          }}
          onClick={handleDialogClose}
        >
          <Box
            onClick={(e) => e.stopPropagation()} // Prevents click from closing the dialog when clicking on the graph
            sx={{
              width: "70%",
              p: 1,
              height: "auto",
              position: "relative",
              cursor: "default",
            }}
          >
            <Card
              sx={{
                border: "1px #ddd solid",
                borderRadius: 1,
                width: "100%",
                pt: 5,
                pb: 4,
                paddingX: 7,
                position: "relative"
              }}
            >
              <ReactApexChart
                type="line"
                options={fullOptions}
                series={apexChartData}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            </Card>
          </Box>
        </DialogContent>
      </Dialog>


    </>
  ) : null
};

export default ResultDetailGraphBulkReview;